import { Controller } from "@hotwired/stimulus";
import {
  browserDeps,
  renderSupportFallback,
  openSupportChat,
  supportBody,
  handToLivePanel,
} from "../lib/supportHandoff.mjs";

// Handles action button clicks in Kit chat messages.
// "send" submits the button's message as a user chat message.
// "dismiss" removes the action buttons from the message.
// "support" hands the user off to a human — Kit hides the Intercom launcher, so
// this block is how a kit-enabled brand actually reaches support. The handoff
// itself lives in lib/supportHandoff.mjs, shared with
// kit_panel_controller#openSupport so the two entry points cannot drift again.
export default class extends Controller {
  static targets = ["hint", "status"];
  static values = { chatId: Number };

  send(event) {
    const message = event.params.message;
    const messageId = event.params.messageId;

    this.removeButtons(messageId);
    this.submitMessage(message);
  }

  dismiss(event) {
    const messageId = event.params.messageId;
    this.removeButtons(messageId);
  }

  // The one click in Kit that promises a person. It used to delete the buttons
  // first and then call Intercom, which meant a click that reached nobody also
  // destroyed the only thing the customer had to click — they were left staring
  // at a message with no affordance and no explanation, which is precisely what
  // "l did that handoff button - not working" was describing.
  //
  // So: close the panel (the messenger takes the same corner, and this mirrors
  // the header Support button), ask, wait for the messenger to actually appear,
  // and only then take the button away. If it never appears, the button stays
  // and the customer gets a link they can act on instead of silence.
  async support(event) {
    const messageId = event.params.messageId;
    const button = event.currentTarget;
    const body = supportBody(event.params.prefill, window.location.href);

    // Live inbox: the panel hands this chat to the team and re-renders on it,
    // acknowledgment included. Nothing to verify — no third party involved.
    if (handToLivePanel(document, window, event.params.prefill)) {
      this.track({ ok: true, reason: "inbox" });
      return;
    }

    // Let the panel get out of the way, exactly as kit-panel#openSupport does.
    // Dispatched rather than reached for: this controller is mounted on a
    // message, and the panel owns whether it is open.
    window.dispatchEvent(new CustomEvent("kit:close"));

    this.setBusy(button, true);
    const result = await openSupportChat(body, browserDeps(window, document));
    this.setBusy(button, false);

    this.track(result);

    if (result.ok) {
      // The composer opens PREFILLED, not sent — Intercom's JS API has no "send
      // this now". Saying so is the difference between a handoff and a customer
      // who thinks they already asked and is waiting for a reply nobody can
      // see: Kit offered this button 27 times between 2026-08-18 and
      // 2026-09-15 and not one Intercom conversation came out of it.
      this.replaceButtons(
        messageId,
        "Support chat opened — press send in that window to start it."
      );
      return;
    }

    // Nothing opened, so the panel we stepped aside a moment ago is the only
    // thing on screen with anything to say. Bring it back before writing into
    // it — a fallback rendered inside a closed drawer is the same silence we
    // are here to remove.
    window.dispatchEvent(new CustomEvent("kit:open"));
    this.showFallback(body);
  }

  // Private

  // Intercom is not here, or it is here and drew nothing. Either way the
  // customer keeps the button (Intercom boots late, and a second click often
  // works) and gets an address that does not depend on it. A real anchor, not
  // `window.location.href = "mailto:…"`: assigning a mailto is a no-op on a
  // desktop with no mail client registered, which is the same silence we are
  // fixing.
  showFallback(body) {
    if (!this.hasStatusTarget) return;

    // "Opens a chat with our team" is no longer true, and leaving it above the
    // apology reads as two instructions fighting each other.
    if (this.hasHintTarget) this.hintTarget.hidden = true;

    renderSupportFallback(this.statusTarget, body);
  }

  replaceButtons(messageId, text) {
    const buttons = document.getElementById(`action_buttons_${messageId}`);
    if (!buttons) return;

    const note = document.createElement("p");
    note.className = "kit-action-buttons__note";
    note.setAttribute("role", "status");
    note.textContent = text;
    buttons.replaceWith(note);
  }

  setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.classList.toggle("loading", busy);
  }

  // Answers "is the handoff button working?" from the data rather than from a
  // customer telling us. Same idiom as the other controllers that report to
  // PostHog; absent outside production, so guarded.
  track(result) {
    if (typeof window.posthog === "undefined") return;

    window.posthog.capture("kit_support_handoff_clicked", {
      chat_id: this.hasChatIdValue ? this.chatIdValue : null,
      opened: Boolean(result.ok),
      reason: result.ok ? "opened" : result.reason,
      path: window.location.pathname,
    });
  }

  removeButtons(messageId) {
    const buttons = document.getElementById(`action_buttons_${messageId}`);
    if (buttons) buttons.remove();
  }

  submitMessage(message) {
    const form = this.findForm();
    if (!form) return;

    const input =
      form.querySelector("textarea[name='message[content]']") ||
      form.querySelector("input[name='message[content]']");
    if (!input) return;

    input.value = message;
    form.requestSubmit();
  }

  findForm() {
    return document.getElementById(`new_message_${this.chatIdValue}`);
  }
}
